import { useEffect, useRef, useState } from 'react'
import { getPoll } from 'src/api/polls'
import { Poll } from 'src/api/types/polls'
import { ViewMode } from 'src/pages/Dashboard'
import { GameSounds } from 'src/ui/sound/types'

export type UsePollStateArgs = {
  viewMode: ViewMode
  gameState: string
  sounds: GameSounds
  setViewMode: (mode: ViewMode) => void
  setSelectedListTab: (tab: string) => void
  homePath?: string
  historyPush: (path: string) => void
  startPollGame: (poll: Poll, options?: { silentStart?: boolean }) => void
  onPollDeleted: (pollId: number) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  err: (error: any) => void
}

export type UsePollStateReturn = {
  selectedPollId: number | undefined
  pollRefreshKey: number
  isPollDetailView: boolean
  handlePollSelect: (poll: Poll) => void
  handlePollVote: (poll: Poll, hasVoted: boolean) => void
  handlePollPlay: (poll: Poll) => void
  handlePollCreated: (pollId: number, pollFormat?: Poll['pollFormat']) => void
  handlePollBack: () => void
  handlePollDeleted: (pollId: number) => void
  resetPollSelection: () => void
  markPollPlayHandled: (pollId: number) => void
  setPollId: (pollId: number | undefined) => void
}

const isPollDetailMode = (viewMode: ViewMode, pollId?: number) =>
  (viewMode === 'poll_detail' || viewMode === 'poll_vote') && !!pollId

export default function usePollState(args: UsePollStateArgs): UsePollStateReturn {
  const {
    viewMode,
    gameState,
    sounds,
    setViewMode,
    setSelectedListTab,
    homePath = '/play/solo',
    historyPush,
    startPollGame,
    onPollDeleted,
    err,
  } = args

  const [selectedPollId, setSelectedPollId] = useState<number | undefined>(undefined)
  const [pollRefreshKey, setPollRefreshKey] = useState(0)
  const pollPlayHandledRef = useRef<number | null>(null)
  const skipNextPollPlayRef = useRef<number | null>(null)
  const startPollGameLatest = useRef(startPollGame)
  startPollGameLatest.current = startPollGame
  const errorLatest = useRef(err)
  errorLatest.current = err

  const isPollPlayView = viewMode === 'poll_play'
  const isPollDetailView = isPollDetailMode(viewMode, selectedPollId)

  useEffect(() => {
    if (!isPollPlayView) {
      pollPlayHandledRef.current = null
      skipNextPollPlayRef.current = null
      return
    }
    if (!selectedPollId) return
    if (skipNextPollPlayRef.current === selectedPollId) {
      skipNextPollPlayRef.current = null
      return
    }
    // An online game stays "pre" until the server acknowledges it; do not restart on rerenders.
    if (pollPlayHandledRef.current === selectedPollId) return
    pollPlayHandledRef.current = selectedPollId
    let current = true

    getPoll(selectedPollId)
      .then((res) => {
        if (!current) return
        if (res.success && res.data) {
          startPollGameLatest.current(res.data)
        } else {
          setViewMode('poll_detail')
          historyPush(`/polls/${selectedPollId}`)
        }
      })
      .catch((e) => {
        if (!current) return
        errorLatest.current(e)
        setViewMode('poll_detail')
        historyPush(`/polls/${selectedPollId}`)
      })
    return () => {
      current = false
    }
  }, [isPollPlayView, selectedPollId])

  const handlePollSelect = (poll: Poll) => {
    sounds.click()
    if (!poll.id) return
    setSelectedPollId(poll.id)
    setViewMode('poll_detail')
    historyPush(`/polls/${poll.id}`)
  }

  const handlePollVote = (poll: Poll, hasVoted: boolean) => {
    sounds.click()
    if (!poll.id) return
    setSelectedPollId(poll.id)
    setViewMode(hasVoted ? 'poll_detail' : 'poll_vote')
    historyPush(hasVoted ? `/polls/${poll.id}` : `/polls/${poll.id}/vote`)
  }

  const handlePollPlay = (poll: Poll) => {
    if (!poll.id) return
    if (skipNextPollPlayRef.current !== poll.id) pollPlayHandledRef.current = null
    setSelectedPollId(poll.id)
    setViewMode('poll_play')
    historyPush(`/polls/${poll.id}/play`)
  }

  const handlePollCreated = (pollId: number, pollFormat?: Poll['pollFormat']) => {
    setSelectedPollId(pollId)
    setSelectedListTab('polls')
    setViewMode(pollFormat === 'community_answers' ? 'poll_detail' : 'poll_vote')
    historyPush(`/polls/${pollId}${pollFormat === 'community_answers' ? '' : '/vote'}`)
  }

  const handlePollBack = () => {
    sounds.click()
    setViewMode('home')
    setSelectedListTab('polls')
    historyPush(homePath)
  }

  const handlePollDeleted = (pollId: number) => {
    setSelectedPollId(undefined)
    setPollRefreshKey((prev) => prev + 1)
    setViewMode('home')
    setSelectedListTab('polls')
    historyPush(homePath)
    onPollDeleted(pollId)
  }

  const resetPollSelection = () => {
    setSelectedPollId(undefined)
    pollPlayHandledRef.current = null
    skipNextPollPlayRef.current = null
  }

  const markPollPlayHandled = (pollId: number) => {
    skipNextPollPlayRef.current = pollId
    pollPlayHandledRef.current = pollId
  }

  return {
    selectedPollId,
    pollRefreshKey,
    isPollDetailView,
    handlePollSelect,
    handlePollVote,
    handlePollPlay,
    handlePollCreated,
    handlePollBack,
    handlePollDeleted,
    resetPollSelection,
    markPollPlayHandled,
    setPollId: setSelectedPollId,
  }
}
